import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
// import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer"; // Poolcorp Custom Code
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
// import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries"; // Poolcorp Custom Code
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Poolcorp Custom Code */
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { baseGridItemGridProps } from "@store/theme/common/props/baseGrid";
import { COMMON, TYPOGRAPHY } from "@store/theme/design";
import breakpointMediaQueries, { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
/* Poolcorp Custom Code */

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: baseGridItemGridProps.container,
    subCategoryItem: baseGridItemGridProps.item,
    innerContainer: {
        gap: 0,
        css: css`
            position: relative;

            &:hover img {
                transform: translateY(-50%) scale(1.2);
                cursor: pointer;
            }

            ${GridOffset} {
                max-width: 100%;
            }
        `,
    },
    subCategoryImageItem: {
        width: 12,
        css: css`
            width: 100%;
            height: inherit;
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
        `,
    },
    subCategoryImage: {
        css: css`
            position: relative;
            width: 100%;
            padding: 0;

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: ["55%", "74%"], properties: ["padding-top"] }],
                    mediaOptions.min,
                )}

            &::before {
                content: "";
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                background: linear-gradient(180deg, rgba(48, 48, 48, 0) 0%, rgba(48, 48, 48, 0.85) 100%);
                z-index: 1;
            }

            img {
                position: absolute;
                top: 50%;
                width: auto;
                max-height: 100%;
                max-width: none;
                transition: transform 1s cubic-bezier(0.215, 0.61, 0.355, 1);
                transform: translateY(-50%) scale(1);
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [10, 10, 12, 12, 12],
        css: css`
            position: absolute;
            bottom: 0;
            padding: 1.15em;
            z-index: 1;
            font-size: 18px;
            pointer-events: none;

            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        css`
                            font-size: 21px;
                        `,
                        css`
                            justify-content: center;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    subCategoryNameLink: {
        typographyProps: {
            color: "common.background",
            lineHeight: 1.2,
            weight: TYPOGRAPHY.weight.bold,
            css: css`
                width: 100%;
                text-align: left;
                color: white;
                word-wrap: break-word;
            `,
        },
        css: css`
            &:hover {
                text-decoration: none;
            }
        `,
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({ category, fields: { showImages } }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId} {...styles.subCategoryItem}>
                    <SubCategoryLink categoryId={subCategoryId} showImages={showImages} />
                </GridItem>
            ))}
        </GridContainer>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    return (
        <GridContainer {...styles.innerContainer}>
            {showImages && (
                <GridItem {...styles.subCategoryImageItem}>
                    <Clickable href={category.path} {...styles.subCategoryImageClickable}>
                        {/* Poolcorp Custom Code */}
                        <LazyImage
                            src={category.smallImagePath || COMMON.defaultCategoryImagePath}
                            altText={category.imageAltText}
                            {...styles.subCategoryImage}
                        />
                        {/* Poolcorp Custom Code */}
                    </Clickable>
                </GridItem>
            )}
            <GridItem {...styles.subCategoryNameLinkItem}>
                <Link
                    href={category.path}
                    {...styles.subCategoryNameLink}
                    data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                >
                    {category.shortDescription}
                </Link>
            </GridItem>
        </GridContainer>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
